import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Stack, Group, TextInput, Button, Table, Text, Badge, ActionIcon,
  Modal, MultiSelect, Select, Tooltip, Loader, Alert, Avatar, Tabs, Switch, Checkbox, Anchor,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconPlus, IconTrash, IconBuilding, IconUser, IconUsersGroup, IconTemplate, IconPhoto, IconArrowsExchange, IconInbox, IconBeach, IconBeachOff, IconShieldOff, IconShieldLock, IconStack2, IconCloudPlus } from '@tabler/icons-react'
import { useOrganizations, useOrganization, useCreateOrganization, useDeleteOrganization, useGestores, useUpdateOrganization } from '@/hooks/useOrganizations'
import { organizationsApi, type OrganizationMember, type OrgUser } from '@/api/organizations'
import { permissionsApi } from '@/api/permissions'
import { formatApiError } from '@/api/errors'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'
import { useQuery } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { WidgetCard } from '@/components/ui'
import { JiraLinkPanel } from '@/components/jira/JiraLinkPanel'
import { OrgRequestsSection, useIncomingPendingCount } from './OrgRequestsSection'
import { TransferUserModal } from './TransferUserModal'
import { LeaveUserModal } from './LeaveUserModal'
import { OrgCapabilityDrawer } from './OrgCapabilityDrawer'
import { AbaTimesDeSoc } from './soc/AbaTimesDeSoc'
import { AbaFilas } from './filas/AbaFilas'
import { CampoSlugDoProjeto, SLUG_PROJETO, slugDoNome } from '@/components/organizacoes/CampoSlugDoProjeto'
import dayjs from 'dayjs'

// Rótulo PT-BR do papel do usuário na organização (evita renderizar "member"/"gestor" crus).
const ROLE_LABEL: Record<string, string> = { member: 'Membro', gestor: 'Gestor' }

// Mensagens PT-BR dos guards do gestor (spec 2026-07-24 §3.2, backend
// org_add_user/org_remove_user). Chave = detail cru retornado pela API.
const ORG_USER_ERROR_MESSAGES: Record<string, string> = {
  use_transfer_flow: 'Este colaborador pertence a outra Organização — use a transferência (em breve).',
  org_catalog_required: 'Defina o catálogo de perfis da Organização ou informe um perfil válido.',
  gestor_cannot_manage_gestor: 'Apenas administradores podem gerenciar gestores/responsável.',
}

/** formatApiError + tradução dos codes dos guards do gestor. */
function formatOrgUserError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && ORG_USER_ERROR_MESSAGES[detail]) {
    return ORG_USER_ERROR_MESSAGES[detail]
  }
  return formatApiError(err, fallback)
}

interface TenantOption {
  value: string
  label: string
}

interface Props {
  tenants: TenantOption[]
  canEditJira?: boolean
  /** false = modo gestor: esconde as ações admin-only (criar/excluir org,
   *  tenants, responsável, logo, Jira). A OrgUsersSection permanece completa
   *  (colaboradores member-only — guards no backend). Default true. */
  isAdmin?: boolean
  /** Restringe os cards às orgs que o usuário gere (modo gestor).
   *  undefined = sem filtro (admin vê todas as visíveis). */
  visibleOrgIds?: number[]
  /** Deep-link (spec §3.5): org a expandir + aba a abrir + solicitação a destacar. */
  deepLinkOrgId?: number | null
  deepLinkTab?: string | null
  deepLinkReqId?: number | null
}

/** A recusa das rotas da conta PRD vem como `detail.mensagem`. */
function mensagemDaRecusa(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  if (detail && typeof detail === 'object' && 'mensagem' in detail) {
    const m = detail.mensagem
    if (typeof m === 'string' && m) return m
  }
  return formatApiError(err, fallback)
}

function LinkDoJob({ texto, jobId }: { texto: string; jobId: number }) {
  return (
    <Text size="sm">
      {texto} (<Anchor href={`/admin/tenants/jobs/${jobId}`} size="sm">job #{jobId}</Anchor>).
    </Text>
  )
}

/** Pede a conta PRD do projeto para uma organizacao que ja existe. */
function ModalContaPrd({ org, opened, onClose }: {
  org: { id: number; name: string; slug: string }
  opened: boolean
  onClose: () => void
}) {
  const [slug, setSlug] = useState(org.slug)
  const [enviando, setEnviando] = useState(false)
  const { data: disp } = useQuery({
    queryKey: ['org-conta-prd-disponibilidade'],
    queryFn: organizationsApi.disponibilidadeContaPrd,
    enabled: opened,
  })
  const disponivel = disp?.disponivel === true

  const confirmar = async () => {
    setEnviando(true)
    try {
      const { job_id } = await organizationsApi.contaPrd(org.id, slug)
      notifications.show({
        title: 'Conta de produção pedida',
        message: <LinkDoJob texto={`Conta de produção de ${org.name} em criação`} jobId={job_id} />,
        color: 'green',
        autoClose: false,
      })
      onClose()
    } catch (err: unknown) {
      notifications.show({ title: 'Erro', message: mensagemDaRecusa(err, 'Falha ao pedir a conta de produção'), color: 'red' })
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Criar conta de produção do projeto">
      <Stack gap="sm">
        {disp && !disponivel && (
          <Alert color="yellow">{disp.motivo ?? 'Você não pode pedir a conta de produção agora.'}</Alert>
        )}
        <CampoSlugDoProjeto valor={slug} onChange={setSlug} />
        <Button onClick={confirmar} loading={enviando} disabled={!disponivel || !SLUG_PROJETO.test(slug)}>
          Confirmar
        </Button>
      </Stack>
    </Modal>
  )
}

/** Organização sem tenant: ninguém dela consegue abrir chamado (todo chamado é de um
 *  tenant). Para organização sem AWS (WhatsApi, KB), o admin cria o tenant de
 *  atendimento — consoleai#26145. */
function AvisoSemTenant({ org }: { org: { id: number; name: string } }) {
  const qc = useQueryClient()
  const [criando, setCriando] = useState(false)
  const criar = async () => {
    setCriando(true)
    try {
      const r = await organizationsApi.tenantDeAtendimento(org.id)
      notifications.show({
        title: 'Tenant de atendimento pronto',
        message: `${org.name} já pode abrir chamados (${r.usuarios_reconciliados.length} colaborador(es) com acesso).`,
        color: 'green',
      })
      qc.invalidateQueries({ queryKey: ['organization', org.id] })
      qc.invalidateQueries({ queryKey: ['organizations'] })
    } catch (err) {
      notifications.show({ title: 'Não foi possível criar', message: formatApiError(err, 'Erro ao criar o tenant de atendimento'), color: 'red' })
    } finally {
      setCriando(false)
    }
  }
  return (
    <Alert color="yellow" title="Esta organização não tem tenant">
      <Stack gap="xs">
        <Text size="sm">
          Sem tenant, os colaboradores dela não conseguem abrir chamados. Se a organização tem
          conta AWS, adicione o tenant dela pelo botão +. Se não tem (WhatsApi, KB), crie o tenant
          de atendimento: ele serve só para os chamados e fica fora de custo, inventário e
          demais rotinas de conta AWS.
        </Text>
        <Group>
          <Button size="xs" onClick={criar} loading={criando}>
            Criar tenant de atendimento
          </Button>
        </Group>
      </Stack>
    </Alert>
  )
}

export function OrganizationManager({ tenants, canEditJira = true, isAdmin = true, visibleOrgIds, deepLinkOrgId, deepLinkTab, deepLinkReqId }: Props) {
  const qc = useQueryClient()
  const { data: orgs, isLoading } = useOrganizations()
  const createOrg = useCreateOrganization()
  const deleteOrg = useDeleteOrganization()

  const { data: gestores } = useGestores()

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newResponsible, setNewResponsible] = useState<string | null>(null)
  // Conta PRD do projeto (A2): o slug nasce do nome ate a pessoa editar.
  const [projectSlug, setProjectSlug] = useState('')
  const [slugEditado, setSlugEditado] = useState(false)
  const [criarConta, setCriarConta] = useState(true)
  const { data: dispConta } = useQuery({
    queryKey: ['org-conta-prd-disponibilidade'],
    queryFn: organizationsApi.disponibilidadeContaPrd,
    enabled: isAdmin && createOpen,
  })
  const contaDisponivel = dispConta?.disponivel === true
  const pedirConta = contaDisponivel && criarConta
  const slugValido = SLUG_PROJETO.test(projectSlug)

  const fecharCriacao = () => {
    setCreateOpen(false)
    setNewName('')
    setNewDesc('')
    setNewResponsible(null)
    setProjectSlug('')
    setSlugEditado(false)
    setCriarConta(true)
  }
  // Deep-link: pré-expande a org indicada na query string (best-effort).
  const [expandedOrg, setExpandedOrg] = useState<number | null>(deepLinkOrgId ?? null)
  const [addMemberOrg, setAddMemberOrg] = useState<number | null>(null)
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])
  const [tenantSearch, setTenantSearch] = useState('')
  const [addingMembers, setAddingMembers] = useState(false)

  const handleCreate = async () => {
    if (!newName.trim() || !newResponsible) return
    if (pedirConta && !slugValido) return
    try {
      const criada = await createOrg.mutateAsync({
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        responsible_user_id: Number(newResponsible),
        ...(pedirConta ? { project_slug: projectSlug, criar_conta_prd: true } : {}),
      })
      const conta = criada?.conta_prd
      if (conta?.job_id != null) {
        notifications.show({
          title: 'Criado',
          message: <LinkDoJob texto="Organização criada; conta de produção em criação" jobId={conta.job_id} />,
          color: 'green',
          autoClose: false,
        })
      } else if (conta?.erro) {
        notifications.show({
          title: 'Organização criada',
          message: `A conta de produção não foi pedida: ${conta.erro}`,
          color: 'yellow',
          autoClose: false,
        })
      } else {
        notifications.show({ title: 'Criado', message: `Organização "${newName}" criada`, color: 'green' })
      }
      fecharCriacao()
    } catch (err: unknown) {
      notifications.show({ title: 'Erro', message: mensagemDaRecusa(err, 'Falha ao criar organização'), color: 'red' })
    }
  }

  const handleDelete = (orgId: number, orgName: string) => {
    modals.openConfirmModal({
      title: 'Excluir organização',
      children: (
        <Text size="sm">
          Excluir a organização <b>{orgName}</b>? Isso remove a configuração Jira
          associada. Esta ação não pode ser desfeita.
        </Text>
      ),
      labels: { confirm: 'Excluir', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteOrg.mutateAsync(orgId)
          notifications.show({ title: 'Excluído', message: `Organização "${orgName}" removida`, color: 'blue' })
        } catch (err) {
          // 409 de quem atende chamado: a mensagem diz o que encaminhar antes (TASK-12185).
          notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao excluir'), color: 'red' })
        }
      },
    })
  }

  // O ambiente vai para a coluna organization_members.environment, cujo CHECK
  // (MIGRATION_142) só aceita minúsculo. Retornar lowercase — enviar MAIÚSCULA
  // estourava 500 (CheckViolation) no backend.
  const extractEnv = (label: string): string | undefined => {
    const match = label.match(/^\[(\w+)\]/)
    return match ? match[1].toLowerCase() : undefined
  }

  const handleAddMembers = async (orgId: number) => {
    if (!selectedTenants.length) return
    setAddingMembers(true)
    let added = 0
    let lastError: any = null
    for (const tenantValue of selectedTenants) {
      const option = tenants.find(t => t.value === tenantValue)
      const env = option ? extractEnv(option.label) : undefined
      try {
        await organizationsApi.addMember(orgId, Number(tenantValue), env)
        added++
      } catch (err: any) {
        // Conflito (409) = tenant já na org → ignora. Demais erros precisam
        // aparecer (antes um catch vazio escondia 500 e dava "nada acontece").
        if (err?.response?.status !== 409) lastError = err
      }
    }
    qc.invalidateQueries({ queryKey: ['organization', orgId] })
    qc.invalidateQueries({ queryKey: ['organizations'] })
    setSelectedTenants([])
    setAddMemberOrg(null)
    setAddingMembers(false)
    if (added > 0) {
      notifications.show({ title: 'Adicionado', message: `${added} tenant(s) adicionado(s) à organização`, color: 'green' })
    }
    if (lastError) {
      notifications.show({ title: 'Erro', message: formatApiError(lastError, 'Falha ao adicionar tenant(s) à organização'), color: 'red' })
    }
  }

  const removerTenant = async (orgId: number, tenantId: number, confirmarSoc: boolean) => {
    await organizationsApi.removeMember(orgId, tenantId, confirmarSoc)
    qc.invalidateQueries({ queryKey: ['organization', orgId] })
    qc.invalidateQueries({ queryKey: ['organizations'] })
    // O time de SOC do tenant sai da vista desta organizacao (TASK-11546).
    qc.invalidateQueries({ queryKey: ['soc-teams'] })
    notifications.show({ title: 'Removido', message: 'Tenant removido da organização', color: 'blue' })
  }

  const handleRemoveMember = (orgId: number, tenantId: number) => {
    modals.openConfirmModal({
      title: 'Remover tenant da organização',
      children: <Text size="sm">Remover este tenant da organização?</Text>,
      labels: { confirm: 'Remover', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await removerTenant(orgId, tenantId, false)
        } catch (err: unknown) {
          // 409 `soc_team_ativo`: o tenant tem time de SOC ativo. O servidor so
          // remove com confirmacao explicita, porque o time sai da vista dos
          // gestores desta organizacao e ninguem entra nele por ela (TASK-11548).
          const resp = (err as {
            response?: { status?: number; data?: { detail?: { code?: string; teams?: { id: number; name: string }[] } } }
          }).response
          if (resp?.status === 409 && resp.data?.detail?.code === 'soc_team_ativo') {
            const nomes = (resp.data.detail.teams ?? []).map((t) => t.name).join(', ')
            modals.openConfirmModal({
              title: 'Tenant com time de SOC ativo',
              children: (
                <Text size="sm">
                  Este tenant tem o time de SOC {nomes} ativo. Ele continua alarmando, mas
                  sai da vista dos gestores desta organização e ninguém entra nele pela
                  organização. Remover mesmo assim?
                </Text>
              ),
              labels: { confirm: 'Remover mesmo assim', cancel: 'Cancelar' },
              confirmProps: { color: 'red' },
              onConfirm: async () => {
                try {
                  await removerTenant(orgId, tenantId, true)
                } catch {
                  notifications.show({ title: 'Erro', message: 'Falha ao remover', color: 'red' })
                }
              },
            })
            return
          }
          notifications.show({ title: 'Erro', message: 'Falha ao remover', color: 'red' })
        }
      },
    })
  }

  // Modo gestor: restringe os cards às orgs que o usuário gere.
  const shownOrgs = visibleOrgIds != null
    ? (orgs ?? []).filter((o) => visibleOrgIds.includes(o.id))
    : (orgs ?? [])

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Text fw={600} size="lg">Organizações</Text>
        {isAdmin && (
          <Button leftSection={<IconPlus size={14} />} onClick={() => setCreateOpen(true)}>
            Nova Organização
          </Button>
        )}
      </Group>

      {isLoading ? (
        <Loader size="sm" />
      ) : !shownOrgs.length ? (
        <Text c="dimmed">{isAdmin ? 'Nenhuma organização criada.' : 'Nenhuma organização gerenciada por você.'}</Text>
      ) : (
        <Stack gap="md">
          {shownOrgs.map((org) => (
            <OrgCard
              key={org.id}
              org={org}
              expanded={expandedOrg === org.id}
              onToggle={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}
              onDelete={() => handleDelete(org.id, org.name)}
              onAddMember={() => setAddMemberOrg(org.id)}
              onRemoveMember={(tid) => handleRemoveMember(org.id, tid)}
              canEditJira={canEditJira}
              isAdmin={isAdmin}
              initialTab={deepLinkOrgId === org.id ? deepLinkTab ?? null : null}
              highlightReqId={deepLinkOrgId === org.id ? deepLinkReqId ?? null : null}
            />
          ))}
        </Stack>
      )}

      <Modal opened={createOpen} onClose={fecharCriacao} title="Nova Organização">
        <Stack gap="sm">
          <TextInput
            label="Nome"
            placeholder="Ex: SAFECHECK"
            value={newName}
            onChange={(e) => {
              const v = e.currentTarget.value
              setNewName(v)
              if (!slugEditado) setProjectSlug(slugDoNome(v))
            }}
          />
          <TextInput label="Descrição (opcional)" value={newDesc} onChange={(e) => setNewDesc(e.currentTarget.value)} />
          <Select
            label="Responsável"
            placeholder="Selecione o gestor responsável"
            data={(gestores ?? []).map(g => ({ value: String(g.id), label: `${g.full_name} (${g.email})` }))}
            value={newResponsible}
            onChange={setNewResponsible}
            searchable
            required
            withAsterisk
          />
          <Checkbox
            label="Criar a conta de produção do projeto"
            checked={pedirConta}
            disabled={!contaDisponivel}
            onChange={(e) => setCriarConta(e.currentTarget.checked)}
            description={
              dispConta && !contaDisponivel
                ? dispConta.motivo ?? 'Você não pode pedir a conta de produção agora.'
                : undefined
            }
          />
          {pedirConta && (
            <CampoSlugDoProjeto
              valor={projectSlug}
              onChange={(v) => { setSlugEditado(true); setProjectSlug(v) }}
            />
          )}
          <Button
            onClick={handleCreate}
            loading={createOrg.isPending}
            disabled={!newName.trim() || !newResponsible || (pedirConta && !slugValido)}
          >
            Criar
          </Button>
        </Stack>
      </Modal>

      <Modal opened={addMemberOrg !== null} onClose={() => { setAddMemberOrg(null); setTenantSearch('') }} title="Adicionar Tenants">
        <Stack gap="sm">
          <MultiSelect
            label="Tenants"
            placeholder="Selecione os tenants"
            data={tenants}
            value={selectedTenants}
            onChange={setSelectedTenants}
            searchable
            searchValue={tenantSearch}
            onSearchChange={setTenantSearch}
            hidePickedOptions
          />
          <Text size="xs" c="dimmed">O ambiente (DEV, HML, QA, PRD) e detectado automaticamente pelo prefixo do nome.</Text>
          <Button
            onClick={() => addMemberOrg && handleAddMembers(addMemberOrg)}
            loading={addingMembers}
            disabled={!selectedTenants.length}
          >
            Adicionar {selectedTenants.length > 0 ? `(${selectedTenants.length})` : ''}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  )
}

function OrgCard({ org, expanded, onToggle, onDelete, onAddMember, onRemoveMember, canEditJira, isAdmin, initialTab, highlightReqId }: {
  org: { id: number; name: string; slug: string; member_count?: number; responsible_user_id?: number | null; responsible_user_name?: string | null; logo_url?: string | null; requires_change_control?: boolean; change_control_environments?: string[]; ticket_code?: string | null }
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  onAddMember: () => void
  onRemoveMember: (tenantId: number) => void
  canEditJira: boolean
  isAdmin: boolean
  initialTab?: string | null
  highlightReqId?: number | null
}) {
  const { data: detail } = useOrganization(expanded ? org.id : undefined)
  const { data: gestores } = useGestores()
  const updateOrg = useUpdateOrganization()
  const qc = useQueryClient()
  const [changingResponsible, setChangingResponsible] = useState(false)
  const [contaPrdOpen, setContaPrdOpen] = useState(false)
  // Ja tem conta de producao vinculada: a acao nao se oferece (o backend recusa
  // de todo jeito se ja houver job da conta do projeto).
  const temPrd = (detail?.members ?? []).some(m => (m.environment ?? '').trim().toLowerCase() === 'prd')
  const [selectedResponsible, setSelectedResponsible] = useState<string | null>(null)
  // Código de protocolo da org (entra no número dos chamados).
  const currentTicketCode = detail?.ticket_code ?? org.ticket_code ?? ''
  const [ticketCode, setTicketCode] = useState('')
  const [ticketCodeError, setTicketCodeError] = useState<string | null>(null)
  const [editingTicketCode, setEditingTicketCode] = useState(false)
  // Aba ativa do card expandido (colaboradores × solicitações). Deep-link §3.5.
  const [tab, setTab] = useState<string>(initialTab === 'solicitacoes' || initialTab === 'filas' ? initialTab : 'colaboradores')
  // Badge de pendências incoming (só carrega quando o card está expandido).
  const pendingCount = useIncomingPendingCount(org.id, expanded)

  // Logo da org: upload presign → PUT direto no S3 → confirm → refetch.
  // Espelha o fluxo do avatar de grupo (chatApi.convAvatar*). O backend é a
  // autoridade da permissão (403 se não puder gerir) — a UI só trata o erro.
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [logoBusy, setLogoBusy] = useState(false)
  const logoUrl = detail?.logo_url ?? org.logo_url ?? undefined

  const refetchLogo = () => {
    qc.invalidateQueries({ queryKey: ['organizations'] })
    qc.invalidateQueries({ queryKey: ['organization', org.id] })
    qc.invalidateQueries({ queryKey: ['organizations-lite'] })
  }

  async function handleLogoPick(file: File) {
    setLogoBusy(true)
    try {
      const presign = await organizationsApi.presignLogo(org.id, {
        filename: file.name,
        mime_type: file.type || 'image/png',
        size_bytes: file.size,
      })
      if (!presign.supported || !presign.upload_url || !presign.s3_key) {
        notifications.show({ color: 'red', title: 'Upload não suportado', message: 'O servidor não suporta upload de logo no momento.' })
        return
      }
      await organizationsApi.uploadLogoToS3(presign.upload_url, file)
      await organizationsApi.confirmLogo(org.id, presign.s3_key)
      refetchLogo()
      notifications.show({ color: 'teal', title: 'Logo atualizado', message: 'Logo da organização atualizado com sucesso.' })
    } catch (err: any) {
      notifications.show({ color: 'red', title: 'Erro no upload', message: formatApiError(err, 'Não foi possível atualizar o logo. Verifique suas permissões.') })
    } finally {
      setLogoBusy(false)
    }
  }

  async function handleLogoRemove() {
    setLogoBusy(true)
    try {
      await organizationsApi.deleteLogo(org.id)
      refetchLogo()
      notifications.show({ color: 'teal', title: 'Logo removido', message: 'Logo da organização removido.' })
    } catch (err: any) {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Não foi possível remover o logo.') })
    } finally {
      setLogoBusy(false)
    }
  }

  // Ausente (resposta antiga, ou banco antes da MIGRATION_219) = exige. Mesmo
  // fail-closed do gate no backend: nao saber nao pode virar "isenta".
  const exigeGmud = (detail?.requires_change_control ?? org.requires_change_control) !== false

  const handleToggleChangeControl = async (proximo: boolean) => {
    try {
      await updateOrg.mutateAsync({ orgId: org.id, data: { requires_change_control: proximo } })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['organization', org.id] })
      notifications.show({
        title: 'Atualizado', color: 'green',
        message: proximo
          ? 'Esta organização volta a exigir GMUD.'
          : 'Esta organização não passa mais por GMUD. A mudança ficou registrada na auditoria.',
      })
    } catch (err: any) {
      notifications.show({ title: 'Erro', color: 'red', message: formatApiError(err, 'Falha ao alterar o controle de mudança') })
    }
  }

  // Ambientes que exigem GMUD (spec 2026-10-08 D9). Ausente = ['prd'], a
  // semente da MIGRATION_382. As opções são a lista salva, 'prd' e os
  // ambientes dos tenants da organização; o backend valida o formato de novo.
  const ambientesSalvos = useMemo(
    () => detail?.change_control_environments ?? org.change_control_environments ?? ['prd'],
    [detail?.change_control_environments, org.change_control_environments],
  )
  const [ambientesGmud, setAmbientesGmud] = useState<string[]>(ambientesSalvos)
  useEffect(() => setAmbientesGmud(ambientesSalvos), [ambientesSalvos])
  const opcoesDeAmbiente = useMemo(() => Array.from(new Set([
    'prd', ...ambientesSalvos,
    ...((detail?.members ?? []).map(m => (m.environment ?? '').trim().toLowerCase()).filter(Boolean)),
  ])).sort(), [detail?.members, ambientesSalvos])
  const ambientesMudaram = [...ambientesGmud].sort().join(',') !== [...ambientesSalvos].sort().join(',')
  const ambienteForaDoFormato = ambientesGmud.find(a => !/^[a-z][a-z0-9_-]{1,7}$/.test(a))
  const erroAmbientes = ambientesGmud.length === 0
    ? 'Escolha ao menos um ambiente, ou desligue o controle de mudança.'
    : ambienteForaDoFormato
      ? `"${ambienteForaDoFormato}": use letras minúsculas, números, "-" ou "_" (2 a 8 caracteres).`
      : undefined

  const handleSaveAmbientesGmud = async () => {
    if (erroAmbientes) return
    try {
      await updateOrg.mutateAsync({ orgId: org.id, data: { change_control_environments: ambientesGmud } })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['organization', org.id] })
      notifications.show({
        title: 'Atualizado', color: 'green',
        message: `GMUD exigida em: ${[...ambientesGmud].sort().join(', ')}. A mudança ficou registrada na auditoria.`,
      })
    } catch (err: any) {
      notifications.show({ title: 'Erro', color: 'red', message: formatApiError(err, 'Falha ao salvar os ambientes que exigem GMUD') })
    }
  }

  const handleSaveTicketCode = async () => {
    const code = ticketCode.trim().toUpperCase()
    // Espelha a validação do backend (ticket_protocol.validate_ticket_code) para
    // dar erro imediato; o backend continua sendo a autoridade.
    if (!/^[A-Z0-9]{2,8}$/.test(code)) {
      setTicketCodeError('Use de 2 a 8 caracteres, apenas letras e números')
      return
    }
    if (/^T\d+$/.test(code)) {
      setTicketCodeError('A forma "T" seguida só de números é reservada')
      return
    }
    setTicketCodeError(null)
    try {
      await updateOrg.mutateAsync({ orgId: org.id, data: { ticket_code: code } })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['organization', org.id] })
      notifications.show({
        title: 'Atualizado',
        message: `Código de protocolo alterado para ${code}. Chamados já abertos mantêm o número atual.`,
        color: 'green',
      })
      setEditingTicketCode(false)
    } catch (err: any) {
      setTicketCodeError(err?.response?.data?.detail ?? 'Não foi possível salvar o código')
    }
  }

  const handleChangeResponsible = async () => {
    if (!selectedResponsible) return
    try {
      await updateOrg.mutateAsync({ orgId: org.id, data: { responsible_user_id: Number(selectedResponsible) } })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['organization', org.id] })
      notifications.show({ title: 'Atualizado', message: 'Responsável alterado com sucesso', color: 'green' })
      setChangingResponsible(false)
      setSelectedResponsible(null)
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao alterar responsável'), color: 'red' })
    }
  }

  return (
    <WidgetCard accent="none" p={0}>
      <Group p="md" justify="space-between" style={{ cursor: 'pointer' }} onClick={onToggle}>
        <Group gap="sm">
          <Avatar src={logoUrl} radius="md" size={32} color="teal">
            <IconBuilding size={18} />
          </Avatar>
          <Text fw={500}>{org.name}</Text>
          <Badge variant="light" size="sm">{org.member_count ?? 0} tenants</Badge>
          {org.responsible_user_name && (
            <Group gap={4}>
              <IconUser size={14} color="gray" />
              <Text size="sm" c="dimmed">{org.responsible_user_name}</Text>
            </Group>
          )}
        </Group>
        {isAdmin && (
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={(e) => { e.stopPropagation(); onAddMember() }}>
              <IconPlus size={12} />
            </Button>
            {!temPrd && (
              <Tooltip label="Criar conta de produção do projeto">
                <ActionIcon
                  variant="subtle"
                  aria-label="Criar conta de produção do projeto"
                  onClick={(e) => { e.stopPropagation(); setContaPrdOpen(true) }}
                >
                  <IconCloudPlus size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Excluir organização">
              <ActionIcon color="red" variant="subtle" onClick={(e) => { e.stopPropagation(); onDelete() }}>
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Group>

      {expanded && detail?.members && (
        <Stack gap="md" mx="md" mb="md">
          {/* Input oculto para envio do logo */}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleLogoPick(file)
              e.target.value = ''
            }}
          />
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Avatar src={logoUrl} radius="md" size={40} color="teal">
                <IconBuilding size={20} />
              </Avatar>
              <Text size="sm" fw={500}>Logo da organização</Text>
            </Group>
            {/* Upload/remoção de logo é admin-only (modo gestor só visualiza). */}
            {isAdmin && (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPhoto size={14} />}
                  loading={logoBusy}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoUrl ? 'Trocar logo' : 'Enviar logo'}
                </Button>
                {logoUrl && (
                  <Tooltip label="Remover logo">
                    <ActionIcon color="red" variant="subtle" loading={logoBusy} onClick={handleLogoRemove} aria-label="Remover logo">
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )}
          </Group>
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconUser size={16} />
              <Text size="sm" fw={500}>Responsável:</Text>
              <Text size="sm">{org.responsible_user_name ?? 'Não definido'}</Text>
            </Group>
            {/* Troca de responsável é admin-only (modo gestor só visualiza). */}
            {!isAdmin ? null : !changingResponsible ? (
              <Button size="xs" variant="light" onClick={() => setChangingResponsible(true)}>Trocar</Button>
            ) : (
              <Group gap="xs">
                <Select
                  size="xs"
                  placeholder="Selecione"
                  data={(gestores ?? []).map(g => ({ value: String(g.id), label: `${g.full_name} (${g.email})` }))}
                  value={selectedResponsible}
                  onChange={setSelectedResponsible}
                  searchable
                  w={250}
                />
                <Button size="xs" onClick={handleChangeResponsible} loading={updateOrg.isPending} disabled={!selectedResponsible}>
                  Salvar
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => { setChangingResponsible(false); setSelectedResponsible(null) }}>
                  Cancelar
                </Button>
              </Group>
            )}
          </Group>
          {/* Controle de mudança: admin-only, como a troca de responsável.
              Desligar aqui isenta a organização INTEIRA de GMUD — inclusive as
              seis ações críticas. Por isso a descrição diz o que acontece, e
              não só o que o campo é. */}
          {isAdmin && (
            <Switch
              label="Exige controle de mudança (GMUD)"
              description={exigeGmud
                ? 'Ações críticas desta organização passam por aprovação do comitê.'
                : 'Desligado: as ações desta organização executam sem aprovação, inclusive as críticas. A mudança fica registrada na auditoria.'}
              checked={exigeGmud}
              disabled={updateOrg.isPending}
              onChange={(e) => handleToggleChangeControl(e.currentTarget.checked)}
            />
          )}
          {/* Abrangência da GMUD (spec 2026-10-08 D9): só com o controle ligado.
              Lista vazia com o controle ligado desligaria a GMUD em silêncio —
              o botão fica desabilitado e o backend responde 422. */}
          {isAdmin && exigeGmud && (
            <Group align="flex-end" gap="xs">
              <MultiSelect
                label="Ambientes que exigem GMUD"
                description="As mudanças em tenants destes ambientes passam pelo comitê."
                data={opcoesDeAmbiente}
                value={ambientesGmud}
                onChange={setAmbientesGmud}
                error={erroAmbientes}
                searchable
                disabled={updateOrg.isPending}
                w={320}
              />
              {ambientesMudaram && (
                <>
                  <Button size="xs" onClick={handleSaveAmbientesGmud} loading={updateOrg.isPending}
                          disabled={!!erroAmbientes}>Salvar ambientes</Button>
                  <Button size="xs" variant="subtle" color="gray"
                          onClick={() => setAmbientesGmud(ambientesSalvos)}>Cancelar</Button>
                </>
              )}
            </Group>
          )}
          {/* Código de protocolo: entra no número dos chamados da org. */}
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Group gap="xs">
                <Text size="sm" fw={500}>Código de protocolo:</Text>
                <Text size="sm" ff="monospace">{currentTicketCode || 'Não definido'}</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Entra no número dos chamados junto do ambiente — ex.:{' '}
                <Text span ff="monospace" size="xs">
                  CHM-{currentTicketCode || 'CODIGO'}-PRD-2026-00001
                </Text>
                . Alterar não renumera chamados já abertos.
              </Text>
            </Stack>
            {!isAdmin ? null : !editingTicketCode ? (
              <Button
                size="xs"
                variant="light"
                onClick={() => { setEditingTicketCode(true); setTicketCode(currentTicketCode); setTicketCodeError(null) }}
              >
                {currentTicketCode ? 'Alterar' : 'Definir'}
              </Button>
            ) : (
              <Group gap="xs" align="flex-start">
                <TextInput
                  size="xs"
                  placeholder="Ex: STORE"
                  value={ticketCode}
                  onChange={(e) => setTicketCode(e.currentTarget.value.toUpperCase())}
                  maxLength={8}
                  error={ticketCodeError}
                  w={140}
                />
                <Button size="xs" onClick={handleSaveTicketCode} loading={updateOrg.isPending} disabled={!ticketCode.trim()}>
                  Salvar
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => { setEditingTicketCode(false); setTicketCodeError(null) }}>
                  Cancelar
                </Button>
              </Group>
            )}
          </Group>
          {isAdmin && detail.members.length === 0 && <AvisoSemTenant org={org} />}
          {/* Tenants: gestor vê read-only (remoção de tenant é admin-only). */}
          <Table fz="sm" verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Ambiente</Table.Th>
                {isAdmin && <Table.Th w={50}></Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.members.map((m: OrganizationMember) => (
                <Table.Tr key={m.tenant_id}>
                  <Table.Td>{m.tenant_name}</Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{m.environment ?? '-'}</Badge></Table.Td>
                  {isAdmin && (
                    <Table.Td>
                      <ActionIcon color="red" variant="subtle" size="sm" onClick={() => onRemoveMember(m.tenant_id)} aria-label={`Remover ${m.tenant_name} da organização`}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {/* Catálogo de templates disponíveis da organização — o gestor
              também gere (gate backend: _can_manage_org no GET/PUT). */}
          <OrgTemplatesSection orgId={org.id} isAdmin={isAdmin} />

          {/* Colaboradores × Solicitações (fila do gestor). Deep-link §3.5. */}
          <Tabs value={tab} onChange={(v) => setTab(v ?? 'colaboradores')}>
            <Tabs.List>
              <Tabs.Tab value="colaboradores" leftSection={<IconUsersGroup size={14} />}>
                Colaboradores
              </Tabs.Tab>
              <Tabs.Tab
                value="solicitacoes"
                leftSection={<IconInbox size={14} />}
                rightSection={pendingCount > 0 ? <Badge size="xs" circle color="red">{pendingCount}</Badge> : null}
              >
                Solicitações
              </Tabs.Tab>
              <Tabs.Tab value="soc" leftSection={<IconShieldLock size={14} />}>
                Times de SOC
              </Tabs.Tab>
              <Tabs.Tab value="filas" leftSection={<IconStack2 size={14} />}>
                Filas de chamado
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="colaboradores" pt="md">
              {/* Usuários da organização (gestor: ações member-only via backend). */}
              <OrgUsersSection orgId={org.id} orgName={org.name} isAdmin={isAdmin} responsibleUserId={org.responsible_user_id ?? null} />
            </Tabs.Panel>
            <Tabs.Panel value="solicitacoes" pt="md">
              <OrgRequestsSection orgId={org.id} highlightReqId={highlightReqId} />
            </Tabs.Panel>
            <Tabs.Panel value="soc" pt="md">
              {/* O gestor não tinha de onde ver os times de SOC dos tenants
                  dele — a tela de SOC vira aba aqui em vez de tela própria
                  (decisão do proprietário, Fase 5b). */}
              <AbaTimesDeSoc orgId={org.id} />
            </Tabs.Panel>
            <Tabs.Panel value="filas" pt="md">
              {/* TASK-12181: aba, e não tela própria, pelo mesmo motivo dos Times de SOC. */}
              <AbaFilas orgId={org.id} isAdmin={isAdmin} />
            </Tabs.Panel>
          </Tabs>

          {/* Configuração Jira é admin-only (modo gestor não exibe o painel). */}
          {isAdmin && (
            <JiraLinkPanel
              organizationId={org.id}
              organizationName={org.name}
              canEdit={canEditJira}
            />
          )}
        </Stack>
      )}
      {isAdmin && contaPrdOpen && (
        <ModalContaPrd org={org} opened={contaPrdOpen} onClose={() => setContaPrdOpen(false)} />
      )}
    </WidgetCard>
  )
}

function OrgTemplatesSection({ orgId, isAdmin = true }: { orgId: number; isAdmin?: boolean }) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<string[] | null>(null)
  const [saving, setSaving] = useState(false)

  // M-10 (TASK-2834): o catálogo GLOBAL de templates de plataforma só é buscado
  // por admin. Gestores não-admin veem esta seção (backend gate _can_manage_org),
  // mas NÃO devem receber a lista de todos os templates da plataforma (vazamento
  // cross-org) — para eles as opções vêm do catálogo org-scoped abaixo.
  const { data: allTemplates = [] } = useQuery({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
    enabled: isAdmin,
  })

  const { data: catalog = [], isLoading, isError: catalogError } = useQuery({
    queryKey: ['org-templates', orgId],
    queryFn: () => organizationsApi.getOrganizationTemplates(orgId),
  })

  // Pre-carrega o MultiSelect com o catalogo atual da org.
  const current = catalog.map(t => String(t.id))
  const value = selected ?? current

  // Admin escolhe de todos os templates da plataforma; gestor escolhe apenas
  // dentro do catálogo org-scoped (rótulos vêm do próprio catálogo).
  const optionSource = isAdmin ? allTemplates : catalog
  const options = optionSource.map(t => ({ value: String(t.id), label: t.name }))

  const dirty =
    selected !== null &&
    (selected.length !== current.length || selected.some(id => !current.includes(id)))

  const handleSave = async () => {
    if (selected === null) return
    setSaving(true)
    try {
      await organizationsApi.setOrganizationTemplates(orgId, selected.map(Number))
      qc.invalidateQueries({ queryKey: ['org-templates', orgId] })
      setSelected(null)
      notifications.show({ title: 'Salvo', message: 'Catálogo de templates atualizado', color: 'green' })
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatApiError(err, 'Falha ao salvar catálogo'), color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <IconTemplate size={16} />
        <Text size="sm" fw={500}>Perfis disponíveis ({catalog.length})</Text>
      </Group>
      <Text size="xs" c="dimmed">
        Perfis que podem ser aplicados aos usuários desta organização. Vazio = sem restrição.
      </Text>
      {catalogError ? (
        <Alert color="red" variant="light">Falha ao carregar o catálogo. Tente novamente.</Alert>
      ) : isLoading ? (
        <Loader size="xs" />
      ) : (
        <Group gap="xs" align="flex-end">
          <MultiSelect
            placeholder="Selecione os perfis disponíveis"
            data={options}
            value={value}
            onChange={setSelected}
            searchable
            clearable
            style={{ flex: 1 }}
          />
          <Button size="sm" disabled={!dirty} loading={saving} onClick={handleSave}>
            Salvar
          </Button>
        </Group>
      )}
    </Stack>
  )
}

// Exportada para teste unitário (OrgUsersSection.test.tsx) — uso em produção
// é interno ao OrganizationManager.
export function OrgUsersSection({ orgId, orgName = '', isAdmin = true, responsibleUserId = null }: {
  orgId: number
  /** Nome da Organização — só para rotular o painel de alçada. */
  orgName?: string
  /** false = caller gestor/responsável (não-admin): só gere MEMBERS — alvos
   *  gestor/responsável ficam read-only e a função concedível é só Membro
   *  (espelha assert_gestor_can_manage_target / assert_org_role_assignable). */
  isAdmin?: boolean
  responsibleUserId?: number | null
}) {
  const qc = useQueryClient()
  const [addingUser, setAddingUser] = useState(false)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>('member')
  // Perfil POR ORG a aplicar na associação (per-org-template §4/§5).
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  // Modal de transferência (spec §3.1) — alvo = colaborador da linha.
  const [transferUser, setTransferUser] = useState<OrgUser | null>(null)
  // Modal de férias (spec §3.3) — alvo = colaborador da linha.
  const [leaveUser, setLeaveUser] = useState<OrgUser | null>(null)
  // Alçada (spec 2026-07-29 §7): admin-only, e só faz sentido para quem gere a
  // org — em quem não é gestor a restrição fica inerte até ele ser promovido.
  const [alcadaUser, setAlcadaUser] = useState<OrgUser | null>(null)

  const { data: orgUsers = [], isError: orgUsersError } = useQuery({
    queryKey: ['org-users', orgId],
    queryFn: () => organizationsApi.listOrgUsers(orgId),
  })

  // Alvo que o caller NÃO pode gerenciar quando não é admin: outros gestores
  // e o responsável da org (guard member-only — 403 gestor_cannot_manage_gestor
  // no backend; aqui é só UX para nem oferecer a ação).
  const isProtectedTarget = (u: OrgUser) =>
    !isAdmin && (u.role === 'gestor' || (responsibleUserId != null && u.user_id === responsibleUserId))

  // Catálogo de perfis DESTA org — alimenta o seletor de perfil por org.
  const { data: orgTemplates = [] } = useQuery({
    queryKey: ['org-templates', orgId],
    queryFn: () => organizationsApi.getOrganizationTemplates(orgId),
  })

  // Pool para adicionar direto (Fase 2): o gestor usa /assignable-users
  // (usuários sem org, pós-settle) — resolve o GAP da Fase 1 em que o /v1/users
  // (escopado por tenant) não devolvia o pool livre para caller não-admin.
  // Admin mantém o /v1/users (comportamento atual). retry:false p/ não martelar 403.
  // M-09 (TASK-2833): buscado só sob demanda (enabled: addingUser) e NUNCA
  // persistido (allowlist em lib/queryCache.ts não inclui estas chaves). Além
  // disso projetamos só id/email/full_name — o backend devolve linhas de usuário
  // completas, mas retemos em memória apenas o mínimo que o seletor precisa.
  const { data: allUsers, isError: allUsersError } = useQuery({
    queryKey: isAdmin ? ['users-for-org-select'] : ['assignable-users', orgId],
    queryFn: async () => {
      if (isAdmin) {
        const res = await (await import('@/api/client')).api.get<{ items: (MixedUserRow & { email: string; full_name: string })[] }>('/v1/users')
        // `/v1/users` mistura usuarios reais com cadastros PENDENTES (`id:
        // null`) — ver utils/realUsers. Sem o filtro, dois pendentes viravam
        // duas opcoes de value "null" e o Mantine derrubava a tela ao abrir
        // "Adicionar" (incidente de 24/08/2026).
        return selectableUsers(res.data.items).map((u) => ({ id: u.id, email: u.email, full_name: u.full_name }))
      }
      return organizationsApi.listAssignableUsers(orgId)
    },
    enabled: addingUser,
    retry: false,
  })

  const existingUserIds = new Set(orgUsers.map(u => u.user_id))
  const availableUsers = (allUsers ?? [])
    .filter(u => !existingUserIds.has(u.id))
    .map(u => ({ value: String(u.id), label: `${u.full_name || u.email} (${u.email})` }))

  const templateOptions = orgTemplates.map(t => ({ value: String(t.id), label: t.name }))
  // Modelo "obrigatório por usuário": quando a org TEM catálogo, cada usuário
  // precisa de um perfil explícito (per-org). Catálogo vazio = sem restrição
  // (cai no perfil global). Ver per-org-template §5 / decisão do owner.
  const templateRequired = templateOptions.length > 0

  const handleAdd = async () => {
    if (!selectedUser) return
    if (templateRequired && !selectedTemplate) return
    try {
      await organizationsApi.addOrgUser(
        orgId,
        Number(selectedUser),
        selectedRole,
        selectedTemplate != null ? Number(selectedTemplate) : undefined,
      )
      qc.invalidateQueries({ queryKey: ['org-users', orgId] })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      notifications.show({ title: 'Adicionado', message: 'Usuário adicionado à organização', color: 'green' })
      setSelectedUser(null)
      setSelectedRole('member')
      setSelectedTemplate(null)
      setAddingUser(false)
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatOrgUserError(err, 'Falha ao adicionar'), color: 'red' })
    }
  }

  // Troca o perfil por-org de um usuário já associado (editor inline na tabela).
  // Reusa o upsert do add_org_user (ON CONFLICT DO UPDATE): preserva a role
  // atual e grava o novo template_id. Validação de catálogo é no backend.
  const handleSetTemplate = async (u: OrgUser, newTemplateId: string | null) => {
    if (!newTemplateId) return
    try {
      await organizationsApi.addOrgUser(orgId, u.user_id, u.role, Number(newTemplateId))
      qc.invalidateQueries({ queryKey: ['org-users', orgId] })
      notifications.show({ title: 'Perfil atualizado', message: `${u.full_name}: perfil da organização alterado`, color: 'green' })
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatOrgUserError(err, 'Falha ao alterar perfil'), color: 'red' })
    }
  }

  const handleRemove = async (userId: number) => {
    try {
      await organizationsApi.removeOrgUser(orgId, userId)
      qc.invalidateQueries({ queryKey: ['org-users', orgId] })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      notifications.show({ title: 'Removido', message: 'Usuário removido da organização', color: 'blue' })
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatOrgUserError(err, 'Falha ao remover'), color: 'red' })
    }
  }

  // Férias (§3.3): a data vem da listagem (users.on_leave_until). Só conta como
  // "de férias" se a data ainda não passou — o backend limpa a coluna por
  // lazy-clear no primeiro acesso do colaborador, então uma data vencida pode
  // aparecer aqui até ele voltar a entrar.
  const leaveUntil = (u: OrgUser): string | null => {
    if (!u.on_leave_until) return null
    const d = dayjs(u.on_leave_until)
    if (!d.isValid() || d.isBefore(dayjs())) return null
    return d.format('DD/MM/YYYY')
  }

  const handleClearLeave = async (u: OrgUser) => {
    try {
      await organizationsApi.clearOrgUserLeave(orgId, u.user_id)
      qc.invalidateQueries({ queryKey: ['org-users', orgId] })
      notifications.show({ title: 'Férias encerradas', message: `${u.full_name || u.email} volta a ter acesso`, color: 'green' })
    } catch (err: any) {
      notifications.show({ title: 'Erro', message: formatOrgUserError(err, 'Falha ao encerrar férias'), color: 'red' })
    }
  }

  // Encerrar férias devolve acesso na hora → confirma (mesmo padrão do remover).
  const confirmClearLeave = (u: OrgUser) => {
    modals.openConfirmModal({
      title: 'Encerrar férias',
      children: (
        <Text size="sm">
          Encerrar as férias de <b>{u.full_name || u.email}</b>? O acesso ao
          ConsoleAI é restabelecido imediatamente.
        </Text>
      ),
      labels: { confirm: 'Encerrar férias', cancel: 'Cancelar' },
      onConfirm: () => { void handleClearLeave(u) },
    })
  }

  // Confirmação antes de remover (gap conhecido: o X removia direto).
  // Padrão do repo: modals.openConfirmModal (mesmo usado p/ excluir org).
  const confirmRemove = (u: OrgUser) => {
    modals.openConfirmModal({
      title: 'Remover colaborador da Organização',
      children: (
        <Text size="sm">
          Remover <b>{u.full_name || u.email}</b> da Organização? O acesso
          concedido por ela será revogado.
        </Text>
      ),
      labels: { confirm: 'Remover', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => { void handleRemove(u.user_id) },
    })
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <IconUsersGroup size={16} />
          <Text size="sm" fw={500}>Usuários ({orgUsers.length})</Text>
        </Group>
        <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={() => setAddingUser(!addingUser)}>
          Adicionar
        </Button>
      </Group>

      {addingUser && allUsersError && (
        <Alert color="yellow" variant="light">
          Não foi possível carregar a lista de usuários da plataforma
          (permissão). Peça a um administrador para adicionar o colaborador,
          ou tente novamente.
        </Alert>
      )}

      {addingUser && (
        <Group gap="xs" align="flex-end">
          <Select
            size="xs"
            label="Usuário"
            placeholder="Buscar usuário..."
            data={availableUsers}
            value={selectedUser}
            onChange={setSelectedUser}
            searchable
            clearable
            style={{ flex: 1 }}
          />
          <Select
            size="xs"
            label="Função"
            // Gestor não concede 'gestor' (assert_org_role_assignable) — só
            // admin vê a opção.
            data={isAdmin
              ? [
                  { value: 'member', label: 'Membro' },
                  { value: 'gestor', label: 'Gestor' },
                ]
              : [{ value: 'member', label: 'Membro' }]}
            value={selectedRole}
            onChange={(v) => setSelectedRole(v ?? 'member')}
            w={120}
          />
          {/* Perfil por org: do catálogo DESTA org. Vazio = sem override
              (cai no perfil global do usuário). */}
          {/* Perfil por org: do catálogo DESTA org. Obrigatório quando a org
              tem catálogo; sem catálogo o campo fica desabilitado (cai no
              perfil global do usuário). */}
          <Select
            size="xs"
            label="Perfil (org)"
            withAsterisk={templateRequired}
            placeholder={templateOptions.length ? 'Selecione o perfil' : 'Sem catálogo'}
            data={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            disabled={templateOptions.length === 0}
            error={templateRequired && !selectedTemplate ? 'Obrigatório' : undefined}
            searchable
            clearable={!templateRequired}
            w={180}
          />
          <Button
            size="xs"
            disabled={!selectedUser || (templateRequired && !selectedTemplate)}
            onClick={handleAdd}
          >
            Adicionar
          </Button>
        </Group>
      )}

      {orgUsersError && (
        <Alert color="red" variant="light">Falha ao carregar os usuários. Tente novamente.</Alert>
      )}
      {!orgUsersError && orgUsers.length === 0 && (
        <Text size="sm" c="dimmed">Nenhum usuário nesta organização.</Text>
      )}
      {orgUsers.length > 0 && (
        <Table fz="sm" verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Usuário</Table.Th>
              <Table.Th>Função</Table.Th>
              <Table.Th>Perfil (org)</Table.Th>
              <Table.Th w={50}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orgUsers.map((u: OrgUser) => (
              <Table.Tr key={u.user_id}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{u.full_name}</Text>
                    {leaveUntil(u) && (
                      <Badge size="xs" variant="light" color="cyan" leftSection={<IconBeach size={10} />}>
                        Férias até {leaveUntil(u)}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">{u.email}</Text>
                </Table.Td>
                <Table.Td><Badge size="xs" variant="light" color={u.role === 'gestor' ? 'orange' : 'blue'}>{ROLE_LABEL[u.role] ?? u.role}</Badge></Table.Td>
                <Table.Td>
                  {isProtectedTarget(u) ? (
                    // Gestor não gere outro gestor/responsável (member-only) —
                    // perfil exibido read-only.
                    <Badge size="xs" variant="light" color={u.template_name ? 'teal' : 'gray'}>
                      {u.template_name ?? 'perfil global'}
                    </Badge>
                  ) : templateOptions.length > 0 ? (
                    // Editor inline: seleciona/troca o perfil POR ORG deste usuário.
                    // Sem valor (template_id null) = "definir perfil" (hoje cai no global).
                    <Select
                      size="xs"
                      placeholder="definir perfil"
                      data={templateOptions}
                      value={u.template_id != null ? String(u.template_id) : null}
                      onChange={(v) => handleSetTemplate(u, v)}
                      description={u.template_id == null ? 'sem perfil · usando global' : undefined}
                      searchable
                      w={200}
                      comboboxProps={{ withinPortal: true }}
                    />
                  ) : (
                    <Badge size="xs" variant="light" color="gray">perfil global</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {isProtectedTarget(u) ? (
                    <Tooltip label="Apenas administradores podem gerenciar gestores/responsável">
                      {/* span: botão disabled tem pointer-events none — sem o
                          wrapper o Tooltip nunca abre. */}
                      <span>
                        <ActionIcon color="gray" variant="subtle" size="sm" disabled aria-label="Remoção indisponível">
                          <IconTrash size={12} />
                        </ActionIcon>
                      </span>
                    </Tooltip>
                  ) : (
                    <Group gap={4} wrap="nowrap">
                      {leaveUntil(u) ? (
                        <Tooltip label="Encerrar férias (restabelece o acesso)">
                          <ActionIcon color="green" variant="subtle" size="sm" onClick={() => confirmClearLeave(u)} aria-label={`Encerrar férias de ${u.full_name || u.email}`}>
                            <IconBeachOff size={12} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Colocar de férias (suspende o acesso até a data)">
                          <ActionIcon color="cyan" variant="subtle" size="sm" onClick={() => setLeaveUser(u)} aria-label={`Colocar ${u.full_name || u.email} de férias`}>
                            <IconBeach size={12} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {isAdmin && (u.role === 'gestor'
                                   || (responsibleUserId != null && u.user_id === responsibleUserId)) && (
                        <Tooltip label="Alçada nesta Organização (mensagens, férias)">
                          <ActionIcon color="orange" variant="subtle" size="sm" onClick={() => setAlcadaUser(u)} aria-label={`Alçada de ${u.full_name || u.email}`}>
                            <IconShieldOff size={12} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Transferir para outra Organização">
                        <ActionIcon color="blue" variant="subtle" size="sm" onClick={() => setTransferUser(u)} aria-label={`Transferir ${u.full_name || u.email}`}>
                          <IconArrowsExchange size={12} />
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon color="red" variant="subtle" size="sm" onClick={() => confirmRemove(u)} aria-label={`Remover ${u.full_name || u.email}`}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <TransferUserModal
        fromOrgId={orgId}
        user={transferUser}
        opened={transferUser !== null}
        onClose={() => setTransferUser(null)}
      />

      <LeaveUserModal
        orgId={orgId}
        user={leaveUser}
        opened={leaveUser !== null}
        onClose={() => setLeaveUser(null)}
      />
      {alcadaUser && (
        <OrgCapabilityDrawer
          opened
          onClose={() => setAlcadaUser(null)}
          orgId={orgId}
          orgName={orgName}
          userId={alcadaUser.user_id}
          userLabel={alcadaUser.full_name || alcadaUser.email}
        />
      )}
    </Stack>
  )
}
